import { ComponentProps, useEffect, useMemo, useRef } from 'react'
import {
  AutocompleteMultiselection,
  YesObject
} from '@yes.technology/react-toolkit'
import useOpenDropdownOnResults from 'site/Renderer/components/FilterFields/hooks/useOpenDropdownOnResults'
import useSuggestionsOnChange from 'site/Renderer/components/FilterFields/hooks/useSuggestionsOnChange'
import SelectAndLinkButton from 'site/Renderer/components/DataTables/components/TableActions/SelectAndLinkButton'
import { RelationAutocompleteContainer } from './RelationAutocomplete.styles'
import SectionDivider from 'site/Renderer/components/SectionDivider/SectionDivider'
import { useTranslation } from 'react-i18n-lite'
import MultiselectionRelation from './MultiselectionRelation'
import UniselectionRelation from './UniselectionRelation'
import isUniselectionMultiplicity from '../../lib/isUniselectionMultiplicity'
import { RelationAutocompleteProps } from './RelationAutocomplete.types'

type Option = ComponentProps<typeof AutocompleteMultiselection>['options'][0]

type GetOptionsParams = {
  selectedObjects: YesObject[]
  suggestions: YesObject[]
}

const getOptions = ({
  selectedObjects,
  suggestions
}: GetOptionsParams): { selectedOptions: Option[]; options: Option[] } => {
  const mappedSuggestions = suggestions.map((suggestion) => {
    return {
      id: suggestion.uuid,
      value: suggestion.des
    }
  })

  const selectedOptions = selectedObjects.map(({ uuid, des }) => ({
    id: uuid,
    value: des
  }))

  const uniqueSelectedOptions = selectedOptions.filter(
    (selectedOption) =>
      !suggestions.find((suggestion) => suggestion.uuid === selectedOption.id)
  )

  const options: Option[] = [...uniqueSelectedOptions, ...mappedSuggestions]

  return { options, selectedOptions }
}

const RelationAutocomplete = ({
  inputRef,
  objectclassUuid,
  onChange,
  selectedObjects,
  informational,
  selectAndLinkOptions,
  selectAndLinkTargetPath,
  hideSelectAndLinkAction,
  title,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  multiplicity,
  id
}: RelationAutocompleteProps) => {
  const isUniselectionRelation = isUniselectionMultiplicity(multiplicity)

  const {
    value,
    onChange: handleChange,
    isLoading,
    suggestions,
    resetValue
  } = useSuggestionsOnChange({
    initialValue: isUniselectionRelation
      ? selectedObjects.at(-1)?.des
      : undefined,
    objectclassUuid,
    skipMetadata: false,
    skipRelations: false
  })

  const internalInputRef = useRef<HTMLInputElement | null>(null)

  const autocompleteInputRef = useMemo(() => {
    if (!inputRef) {
      return internalInputRef
    }

    const fn = (el: HTMLInputElement | null) => {
      internalInputRef.current = el
      if (typeof inputRef === 'function') {
        inputRef(el)
      } else {
        ;(inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
          el
      }
    }
    Object.defineProperty(fn, 'current', {
      get: () => internalInputRef.current,
      configurable: true,
      enumerable: true
    })
    return fn
  }, [inputRef])

  useOpenDropdownOnResults(isLoading, suggestions, internalInputRef)

  useEffect(() => {
    if (selectedObjects.length === 0) {
      resetValue()
    }
  }, [selectedObjects, resetValue])

  const { options, selectedOptions } = getOptions({
    selectedObjects,
    suggestions
  })

  const { t } = useTranslation()
  const label = t('object-view.description')

  const statusClass = isLoading ? 'processing' : 'default'

  return (
    <SectionDivider
      id={id}
      title={title}
      isCollapsibleCard={false}
      backgroundColor={navigationSliderBackgroundColor}
      color={navigationSliderColor}
      isVisible
      hideCollapsibleCardArrow
    >
      <RelationAutocompleteContainer>
        {isUniselectionRelation ? (
          <UniselectionRelation
            onChangeObjects={onChange}
            onChangeValue={handleChange}
            {...{
              inputRef: autocompleteInputRef,
              label,
              options,
              statusClass,
              suggestions,
              value,
              informational
            }}
          />
        ) : (
          <MultiselectionRelation
            onChangeObjects={onChange}
            onChangeValue={handleChange}
            {...{
              inputRef: autocompleteInputRef,
              label,
              options,
              statusClass,
              suggestions,
              selectedObjects,
              selectedOptions,
              value,
              informational
            }}
          />
        )}

        {!informational && (
          <SelectAndLinkButton
            variant='icon'
            {...{
              objectclassUuid,
              objectClassTitle: title,
              selectAndLinkOptions,
              selectAndLinkTargetPath,
              hideSelectAndLinkAction
            }}
          />
        )}
      </RelationAutocompleteContainer>
    </SectionDivider>
  )
}

export default RelationAutocomplete
